import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/@/components/ui/dropdown-menu';
import useAuth from 'src/hooks/useAuth';
import propostasService from 'src/services/propostasService';
import { PROPOSTAS_DESIGN } from 'src/pages/dashboard/Propostas/Propostas.design';
import { caminhoEdicao, iconeTipo, ROTA_NOVO_ORCAMENTO, ROTA_NOVA_PROPOSTA } from 'src/pages/dashboard/Propostas/propostasRotas';

function formatCurrency(value) {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

// onAbrirProposta: quem renderiza decide o que fazer com o clique. No ModalNegocio
// ele troca esta lista pela visualização da proposta no mesmo painel, em vez de
// navegar para o editor — sair da rota descarrega o modal inteiro e voltar é lento.
export default function DrawerNegocioPropostas({ lead, onAbrirProposta }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [propostas, setPropostas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (lead?._id && user?.clienteId) loadPropostas();
  }, [lead?._id]);

  const loadPropostas = async () => {
    try {
      setLoading(true);
      const response = await propostasService.listar(user?.clienteId, { lead_id: lead._id });
      if (!response.error) setPropostas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar propostas do lead:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStatusBadge = (status) => {
    const cfg = PROPOSTAS_DESIGN.status[status] || PROPOSTAS_DESIGN.status.rascunho;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md ${cfg.bg} ${cfg.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
        {cfg.label}
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#8b8d90]">Propostas</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              className="h-7 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[11px] rounded-lg px-2.5"
            >
              <Iconify icon="eva:plus-fill" width={13} className="mr-1" />
              Criar
              <Iconify icon="eva:chevron-down-fill" width={12} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[220px] p-1">
            <DropdownMenuItem
              className="cursor-pointer rounded-lg px-2 py-1.5 gap-2.5 focus:bg-violet-50/70 dark:focus:bg-violet-500/10"
              onClick={() => navigate(`${ROTA_NOVO_ORCAMENTO}?lead_id=${lead._id}&voltar=negocio`)}
            >
              <Iconify icon="mdi:receipt-text-outline" width={15} className="text-[#7C3AED] flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-gray-900 dark:text-[#D1D2D3] leading-tight">Orçamento</p>
                <p className="text-[10px] text-gray-400 leading-tight mt-0.5">Formulário rápido com prévia</p>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer rounded-lg px-2 py-1.5 gap-2.5 focus:bg-violet-50/70 dark:focus:bg-violet-500/10"
              onClick={() => navigate(`${ROTA_NOVA_PROPOSTA}?lead_id=${lead._id}&voltar=negocio`)}
            >
              <Iconify icon="mdi:file-document-edit-outline" width={15} className="text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-gray-900 dark:text-[#D1D2D3] leading-tight">Proposta comercial</p>
                <p className="text-[10px] text-gray-400 leading-tight mt-0.5">Editor completo</p>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {loading ? (
        <p className="text-xs text-gray-400">Carregando...</p>
      ) : propostas.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Nenhuma proposta vinculada a esta oportunidade ainda.</p>
      ) : (
        <div className="space-y-2">
          {propostas.map(proposta => (
            <button
              key={proposta._id}
              onClick={() => onAbrirProposta
                ? onAbrirProposta(proposta)
                : navigate(caminhoEdicao(proposta, `lead_id=${lead._id}&voltar=negocio`))}
              className="w-full flex items-center gap-2.5 p-2.5 rounded-xl border border-gray-100 dark:border-[#383838] hover:border-[#7C3AED]/40 hover:bg-violet-50/40 dark:hover:bg-violet-500/5 transition-colors text-left"
            >
              <Iconify icon={iconeTipo(proposta)} width={16} className="text-[#7C3AED] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">{proposta.titulo}</p>
                <p className="text-xs text-gray-500 dark:text-[#ABABAD]">{formatCurrency(proposta.valor_total)}</p>
              </div>
              {renderStatusBadge(proposta.status)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
